import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Tooltip,
  useTheme,
  alpha,
  Zoom,
} from '@mui/material';
import {
  CheckCircle,
  AddCircleOutline,
} from '@mui/icons-material';
import {
  XIcon,
  TelegramIcon,
  DiscordIcon,
  InstagramIcon,
  GitHubIcon,
  LinkedInIcon,
} from '../icons/SocialIcons'; // Ensure path is correct
import type { SocialLinks } from '../../types';

interface SocialLinksInputProps {
  socialLinks: SocialLinks;
  onChange: (socialLinks: SocialLinks) => void;
  disabled?: boolean;
}

const SocialLinksInput: React.FC<SocialLinksInputProps> = ({
  socialLinks,
  onChange,
  disabled = false,
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<{ key: keyof SocialLinks; label: string; icon: React.ReactNode; prefix: string; color: string; } | null>(null);
  const [inputValue, setInputValue] = useState('');

  const platforms = [
    { key: 'twitter' as keyof SocialLinks, label: 'X (Twitter)', icon: <XIcon fontSize="large" />, color: '#000000', prefix: 'https://x.com/' },
    { key: 'telegram' as keyof SocialLinks, label: 'Telegram', icon: <TelegramIcon fontSize="large" />, color: '#229ED9', prefix: 'https://t.me/' },
    { key: 'discord' as keyof SocialLinks, label: 'Discord', icon: <DiscordIcon fontSize="large" />, color: '#5865F2', prefix: '' },
    { key: 'instagram' as keyof SocialLinks, label: 'Instagram', icon: <InstagramIcon fontSize="large" />, color: '#E4405F', prefix: 'https://instagram.com/' },
    { key: 'github' as keyof SocialLinks, label: 'GitHub', icon: <GitHubIcon fontSize="large" />, color: '#181717', prefix: 'https://github.com/' },
    { key: 'linkedin' as keyof SocialLinks, label: 'LinkedIn', icon: <LinkedInIcon fontSize="large" />, color: '#0A66C2', prefix: 'https://linkedin.com/in/' },
  ];

  const handleOpen = (platform: typeof platforms[0]) => {
    if (disabled) return;
    setSelectedPlatform(platform);
    setInputValue(socialLinks[platform.key] || '');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedPlatform(null);
    setInputValue('');
  };

  const handleSave = () => {
    if (selectedPlatform) {
      let finalValue = inputValue.trim();
      
      // Basic normalization logic
      if (finalValue && !finalValue.startsWith('http') && selectedPlatform.prefix) {
         // If user typed "kaif_tokare", prepending prefix is optional but requested behavior implies handling both.
         // We will store it as is, or you can force the full URL here.
         // If the user wants "only that platforms username OR link", we usually store the clean value.
      }

      const newLinks = { ...socialLinks, [selectedPlatform.key]: finalValue };
      if (!finalValue) delete newLinks[selectedPlatform.key]; // Remove if empty
      
      onChange(newLinks);
      handleClose();
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {platforms.map((platform) => {
          const isConnected = !!socialLinks[platform.key];
          // Handle dark mode color for X/Github
          const displayColor = theme.palette.mode === 'dark' && (platform.color === '#000000' || platform.color === '#181717')
            ? '#FFFFFF' 
            : platform.color;

          return (
            <Tooltip title={isConnected ? `Edit ${platform.label}` : `Connect ${platform.label}`} key={platform.key} TransitionComponent={Zoom}>
              <Box sx={{ position: 'relative' }}>
                <IconButton
                  onClick={() => handleOpen(platform)}
                  disabled={disabled}
                  sx={{
                    bgcolor: isConnected ? alpha(displayColor, 0.1) : alpha(theme.palette.action.disabledBackground, 0.1),
                    color: isConnected ? displayColor : theme.palette.text.disabled,
                    border: `2px solid ${isConnected ? displayColor : 'transparent'}`,
                    p: 1.5,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: alpha(displayColor, 0.2),
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  {platform.icon}
                </IconButton>
                {isConnected && (
                  <CheckCircle 
                    sx={{ 
                      position: 'absolute', 
                      bottom: -4, 
                      right: -4, 
                      fontSize: 18, 
                      color: theme.palette.success.main,
                      bgcolor: theme.palette.background.paper,
                      borderRadius: '50%' 
                    }} 
                  />
                )}
                {!isConnected && !disabled && (
                   <AddCircleOutline
                   sx={{ 
                     position: 'absolute', 
                     bottom: -4, 
                     right: -4, 
                     fontSize: 18, 
                     color: theme.palette.text.secondary,
                     bgcolor: theme.palette.background.paper,
                     borderRadius: '50%' 
                   }} 
                 />
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      {/* Modal for entering link */}
      <Dialog 
        open={open} 
        onClose={handleClose} 
        fullWidth 
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: theme.palette.background.paper,
            backgroundImage: 'none',
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <Box sx={{ color: selectedPlatform?.color === '#000000' && theme.palette.mode === 'dark' ? '#fff' : selectedPlatform?.color }}>
             {selectedPlatform?.icon}
          </Box>
          <Typography variant="h6" fontWeight={700}>
            {selectedPlatform?.label}
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter your {selectedPlatform?.label} username or full profile link.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            variant="filled"
            label={selectedPlatform?.label + " Link/Username"}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={selectedPlatform?.prefix ? `${selectedPlatform.prefix}username` : "username"}
            InputProps={{
              disableUnderline: true,
              sx: { borderRadius: 2 }
            }}
          />
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={handleClose} color="inherit" sx={{ fontWeight: 600 }}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            sx={{ 
              bgcolor: selectedPlatform?.color === '#000000' && theme.palette.mode === 'dark' ? '#fff' : selectedPlatform?.color,
              color: selectedPlatform?.color === '#000000' && theme.palette.mode === 'dark' ? '#000' : '#fff',
              fontWeight: 600,
              px: 3
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SocialLinksInput;